import { PICK_EVENT_TYPES, PICKING_STATUS } from "../constants/PickingConstant";
import { parseArticleCode, productKey, weightLabel } from "./articleCode";

/**
 * The shortage list ("Tekorten"): the orders of a pick day that left the freezer short, for
 * whoever calls the customers. Today the warehouse asks them whether they want the rest later,
 * their money back or a replacement (planning/magazijn-inrichten.md); what was agreed is kept on
 * the order as `picking.shortage`, next to what the scanners reported.
 *
 * Read from `orders/{id}.picking`, the CMS's projection of the scanners' events: a line picked
 * short - nearly always a vak that was empty (VAK LEEG) - or an order the picker aborted.
 *
 * Pure: no Firestore, no React.
 */

/** What can be agreed with the customer. "unreachable" means: call again. */
export const SHORTAGE_OUTCOMES = ["unreachable", "later", "refund", "replacement"];

const SHORT = new Set([PICKING_STATUS.PARTIAL, PICKING_STATUS.ABORTED]);

/** The name the customer knows: the catalogue's, else the order line's, else the code itself. */
const describeArticle = (articleCode, order, productIndex) => {
  const parsed = parseArticleCode(articleCode);
  if (!parsed) return { name: articleCode, weight: null };
  const product = productIndex ? productIndex.get(productKey(parsed.shopSource, parsed.productId)) : null;
  const line = (order.products || []).find((candidate) => candidate && candidate.productId === parsed.productId);
  return { name: (product && product.name) || (line && line.name) || articleCode, weight: parsed.weight };
};

const byRound = (a, b) =>
  (a.order.picking.waveNumber || 0) - (b.order.picking.waveNumber || 0) ||
  (a.order.picking.waveSequence || 0) - (b.order.picking.waveSequence || 0) ||
  String(a.order.orderId || "").localeCompare(String(b.order.orderId || ""));

/**
 * @param {Object[]} orders - the orders of a pick day, with `shopSource` (usePickDayOrders)
 * @param {{productIndex?: Map}} options - from buildProductIndex, for the names
 * @returns {Array<{order, orderRef, aborted, lines, missingUnits, handling}>} in round order;
 *   `lines`: per article short `{ articleCode, name, weight, weightLabel, missing, expected,
 *   picked, emptyLocation, location }`, `location` being the vak reported empty.
 */
export const findShortages = (orders, { productIndex } = {}) =>
  (orders || [])
    .filter((order) => !!order && !!order.picking && SHORT.has(order.picking.status))
    .map((order) => {
      const { picking } = order;
      const emptied = new Map();
      (picking.exceptions || []).forEach((exception) => {
        if (exception && exception.type === PICK_EVENT_TYPES.LOCATION_EMPTY && exception.articleCode) {
          emptied.set(exception.articleCode, exception.location || null);
        }
      });
      const lines = Object.entries(picking.lines || {})
        .filter(([, line]) => !!line && line.picked < line.expected)
        .map(([articleCode, line]) => {
          const { name, weight } = describeArticle(articleCode, order, productIndex);
          return {
            articleCode,
            name,
            weight,
            weightLabel: weightLabel(weight),
            missing: line.expected - line.picked,
            expected: line.expected,
            picked: line.picked,
            emptyLocation: emptied.has(articleCode),
            location: emptied.get(articleCode) || null,
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name, "nl") || String(a.weight || "").localeCompare(String(b.weight || ""), "nl", { numeric: true }));
      return {
        order,
        orderRef: `${order.shopSource}/${order.id}`,
        aborted: picking.status === PICKING_STATUS.ABORTED,
        lines,
        missingUnits: lines.reduce((total, line) => total + line.missing, 0),
        handling: picking.shortage && picking.shortage.outcome ? picking.shortage : null,
      };
    })
    // An order closed as partial but with every line complete after all is no shortage
    .filter((entry) => entry.aborted || entry.lines.length > 0)
    .sort(byRound);

/** Still to call: nothing agreed yet, or the customer did not answer. */
export const isShortageOpen = (entry) => !entry.handling || entry.handling.outcome === "unreachable";

export const summariseShortages = (entries) => ({
  orders: (entries || []).length,
  units: (entries || []).reduce((total, entry) => total + entry.missingUnits, 0),
  open: (entries || []).filter(isShortageOpen).length,
});
